import {
    MutableRefObject, UIEvent as ReactUIEvent, useCallback, useRef, useState
} from 'react';


export type BreakpointNodesRef = MutableRefObject<(HTMLElement | null)[]>;

/**
 * Given an array of breakpoint nodes, this hook will return a handler to track scroll position on the breakpoint
 * node's scroll parent. The current node returned by this hook will be updated when the scroll parent is scrolled
 * past the offsetTop of a breakpoint node. The scroll direction is updated to reflect the direction that the user
 * was scrolling when the current node's breakpoint threshold was crossed. The isScrolledDown flag is set to true
 * when the scroll container's scrollTop is not 0.
 *
 * @param breakpointNodesRef - A ref that holds an array of HTMLElements to use as breakpoint nodes
 */
export function useScrollBreakpoints(breakpointNodesRef: BreakpointNodesRef) {
    const lastScrollTopRef = useRef(0);
    const animFrameRef = useRef<ReturnType<typeof requestAnimationFrame>>();

    const [ currentNode, setCurrentNode ] = useState<HTMLElement>();
    const [ scrollDirection, setScrollDirection ] = useState<'up' | 'down'>('down');
    const [ isScrolledDown, setIsScrolledDown ] = useState(false);

    const handleScroll = useCallback((event: ReactUIEvent<Element, UIEvent>) => {
        const scrollContainer = event.target as HTMLElement;

        if (animFrameRef.current) {
            cancelAnimationFrame(animFrameRef.current);
        }

        animFrameRef.current = requestAnimationFrame(() => {
            if (scrollContainer) {
                const { offsetTop, scrollTop } = scrollContainer;

                const lastScrollTop = lastScrollTopRef.current;
                lastScrollTopRef.current = scrollTop;

                const effectiveContainerOffset = offsetTop + scrollTop;

                const stuckNode = breakpointNodesRef.current?.filter(filterNull)
                    .find((node, index, nodes) => {
                        const nextNode = nodes[index + 1];

                        return node.offsetTop < effectiveContainerOffset
                            && (!nextNode || nextNode.offsetTop >= effectiveContainerOffset);
                    });

                setScrollDirection(scrollTop > lastScrollTop ? 'down' : 'up');
                setIsScrolledDown(scrollTop !== 0);
                setCurrentNode(stuckNode);
            }
        });
    },   [ breakpointNodesRef ]);

    return {
        currentNode,
        handleScroll,
        scrollDirection,
        isScrolledDown
    };
}

function filterNull(node: HTMLElement | null): node is HTMLElement {
    return !!node;
}
